<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { channelColor, channelDisplayName } from '../helpers/timeline';

const props = defineProps({
  channel: { type: Object, required: true },
  // People shown in the avatar stack (members, or the whole team if public).
  people: { type: Array, default: () => [] },
  panel: { type: String, default: null },
  canManage: { type: Boolean, default: false },
  statusFor: { type: Function, required: true },
});

const emit = defineEmits(['openPanel', 'toggleMute', 'edit', 'delete', 'back']);

const { t } = useI18n();
const showMenu = ref(false);

const MAX_STACK = 3;
const stack = computed(() => props.people.slice(0, MAX_STACK));
const extra = computed(() => Math.max(props.people.length - MAX_STACK, 0));
const onlineCount = computed(
  () =>
    props.people.filter(person =>
      ['online', 'busy'].includes(props.statusFor(person.id))
    ).length
);

const ACTIONS = [
  { panel: 'search', icon: 'i-lucide-search', label: 'TEAM_CHAT.SEARCH' },
  { panel: 'pinned', icon: 'i-lucide-pin', label: 'TEAM_CHAT.PINNED' },
  { panel: 'starred', icon: 'i-lucide-star', label: 'TEAM_CHAT.STARRED' },
];

const runMenu = action => {
  showMenu.value = false;
  emit(action);
};
</script>

<template>
  <header
    class="flex items-center flex-shrink-0 h-16 gap-3 px-3 border-b md:px-5 border-n-weak"
  >
    <button
      type="button"
      class="flex items-center justify-center rounded-lg md:hidden size-8 text-n-slate-11 hover:bg-n-alpha-2"
      :title="t('TEAM_CHAT.BACK')"
      @click="emit('back')"
    >
      <span class="size-4 i-lucide-arrow-left" />
    </button>
    <span
      class="flex items-center justify-center flex-shrink-0 rounded-xl size-9"
      :class="channelColor(channel)"
    >
      <span
        class="size-4"
        :class="channel.private ? 'i-lucide-lock' : 'i-lucide-hash'"
      />
    </span>
    <button
      type="button"
      class="flex-1 min-w-0 text-start"
      @click="emit('openPanel', 'about')"
    >
      <h1 class="text-base font-semibold truncate text-n-slate-12">
        {{ channelDisplayName(channel) }}
      </h1>
      <p class="flex items-center gap-1.5 text-xs truncate text-n-slate-11">
        <span
          v-if="onlineCount"
          class="inline-flex items-center flex-shrink-0 gap-1 text-n-teal-11"
        >
          <span class="rounded-full size-1.5 bg-n-teal-9" />
          {{ t('TEAM_CHAT.ONLINE_COUNT', { n: onlineCount }) }}
        </span>
        <span v-if="onlineCount && channel.description">·</span>
        <span v-if="channel.description" class="truncate">
          {{ channel.description }}
        </span>
      </p>
    </button>

    <button
      v-if="people.length"
      type="button"
      class="items-center hidden -space-x-1.5 lg:flex me-1"
      :title="t('TEAM_CHAT.MEMBERS')"
      @click="emit('openPanel', 'members')"
    >
      <Avatar
        v-for="person in stack"
        :key="person.id"
        :src="person.thumbnail"
        :name="person.available_name || person.name || '?'"
        :status="statusFor(person.id)"
        hide-offline-status
        :size="28"
        rounded-full
        class="ring-2 ring-n-solid-1"
      />
      <span
        v-if="extra"
        class="grid text-xs font-semibold rounded-full size-7 place-content-center ring-2 ring-n-solid-1 bg-n-alpha-2 text-n-slate-11"
      >
        +{{ extra }}
      </span>
    </button>

    <div class="relative flex items-center flex-shrink-0 gap-0.5 text-n-slate-11">
      <button
        v-for="action in ACTIONS"
        :key="action.panel"
        type="button"
        class="items-center justify-center hidden rounded-lg sm:flex size-8 hover:bg-n-alpha-2"
        :class="panel === action.panel ? 'bg-n-alpha-2 text-n-slate-12' : ''"
        :title="t(action.label)"
        @click="emit('openPanel', action.panel)"
      >
        <span class="size-4" :class="action.icon" />
      </button>
      <button
        type="button"
        class="flex items-center justify-center rounded-lg size-8 hover:bg-n-alpha-2"
        :class="channel.muted ? 'text-n-amber-11' : ''"
        :title="channel.muted ? t('TEAM_CHAT.UNMUTE') : t('TEAM_CHAT.MUTE')"
        @click="emit('toggleMute')"
      >
        <span
          class="size-4"
          :class="channel.muted ? 'i-lucide-bell-off' : 'i-lucide-bell'"
        />
      </button>
      <button
        type="button"
        class="flex items-center justify-center rounded-lg size-8 hover:bg-n-alpha-2"
        :class="
          panel === 'about' || panel === 'members'
            ? 'bg-n-alpha-2 text-n-slate-12'
            : ''
        "
        :title="t('TEAM_CHAT.INFO')"
        @click="emit('openPanel', 'about')"
      >
        <span class="size-4 i-lucide-info" />
      </button>
      <button
        v-if="canManage"
        type="button"
        class="flex items-center justify-center rounded-lg size-8 hover:bg-n-alpha-2"
        :title="t('TEAM_CHAT.MANAGE')"
        @click="showMenu = !showMenu"
      >
        <span class="size-4 i-lucide-ellipsis-vertical" />
      </button>
      <div
        v-if="showMenu"
        v-on-click-outside="() => (showMenu = false)"
        class="absolute z-20 p-1 mt-1 border shadow-lg w-44 end-0 top-full rounded-xl bg-n-solid-1 border-n-weak"
      >
        <button
          type="button"
          class="flex items-center w-full gap-2 px-2.5 py-2 text-sm rounded-lg text-start text-n-slate-12 hover:bg-n-alpha-2"
          @click="runMenu('edit')"
        >
          <span class="size-4 i-lucide-pencil text-n-slate-11" />
          {{ t('TEAM_CHAT.EDIT') }}
        </button>
        <button
          type="button"
          class="flex items-center w-full gap-2 px-2.5 py-2 text-sm rounded-lg text-start text-n-ruby-11 hover:bg-n-ruby-3"
          @click="runMenu('delete')"
        >
          <span class="size-4 i-lucide-trash-2" />
          {{ t('TEAM_CHAT.DELETE') }}
        </button>
      </div>
    </div>
  </header>
</template>
